import _asyncToGenerator from "@babel/runtime/helpers/asyncToGenerator";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import _regeneratorRuntime from "@babel/runtime/regenerator";
import { useCallback, useMemo, useRef, useState } from 'react';
import { appendSessionSearchParams, fetchTokenInfo, getEndpoints } from "../../../../../shared/api";
import { useAppDispatch, useAuthProvider } from "../../../../../shared/model";
import { SocketStatus } from "../../../../../shared/config";
import { setReconnectCount, setSocketStatus as setStoreStatus } from "../../../model/redux";
var webSocketEvents = ['open', 'close', 'error', 'message'];
export var useSocket = function useSocket(config, token) {
  var baseUrl = config.baseUrl,
    authType = config.authType,
    authZoneConfig = config.authZoneConfig;
  var _useState = useState(SocketStatus.idle),
    _useState2 = _slicedToArray(_useState, 2),
    socketStatus = _useState2[0],
    setSocketStatus = _useState2[1];
  var dispatch = useAppDispatch();
  var handlersRef = useRef({
    open: [],
    close: [],
    message: [],
    error: []
  });
  var socketRef = useRef(null);
  var _useAuthProvider = useAuthProvider(),
    _useAuthProvider2 = _slicedToArray(_useAuthProvider, 2),
    _ = _useAuthProvider2[0],
    setToken = _useAuthProvider2[1];
  var websocketUrl = appendSessionSearchParams({
    baseUrl: getEndpoints(baseUrl).ws,
    token: token,
    authType: authType
  });
  var setStatus = function setStatus(status) {
    setSocketStatus(status);
    dispatch(setStoreStatus(status));
  };
  var onAction = function onAction(type) {
    return function (e) {
      if (type === 'open' || type === 'close') {
        setStatus(type === 'open' ? SocketStatus.open : SocketStatus.closed);
      }
      handlersRef.current[type].forEach(function (func) {
        return func(e);
      });
    };
  };
  var connectWebSocket = useCallback(function (params) {
    return new Promise(function (resolve, reject) {
      try {
        var _params$onSuccess;
        setStatus(SocketStatus.connecting);
        var newSocket = new WebSocket(websocketUrl);
        webSocketEvents.forEach(function (type) {
          newSocket.addEventListener(type, onAction(type));
        });
        resolve(newSocket);
        params === null || params === void 0 || (_params$onSuccess = params.onSuccess) === null || _params$onSuccess === void 0 || _params$onSuccess.call(params);
      } catch (e) {
        var _params$onFailure;
        params === null || params === void 0 || (_params$onFailure = params.onFailure) === null || _params$onFailure === void 0 || _params$onFailure.call(params);
        reject(e);
      }
    });
  }, [websocketUrl]);
  var reconnectWebSocket = function reconnectWebSocket() {
    var _socketRef$current;
    (_socketRef$current = socketRef.current) === null || _socketRef$current === void 0 || _socketRef$current.close();
    dispatch(setReconnectCount(0));
    connectWebSocket().catch(console.error);
  };
  var reconnect = useCallback( /*#__PURE__*/function () {
    var _ref = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee(params) {
      var _data$View, _params$onSuccess2, data, newToken, _params$onFailure2;
      return _regeneratorRuntime.wrap(function _callee$(_context) {
        while (1) switch (_context.prev = _context.next) {
          case 0:
            if (!((authZoneConfig === null || authZoneConfig === void 0 ? void 0 : authZoneConfig.zoneType) === 'authorized')) {
              _context.next = 16;
              break;
            }
            _context.prev = 1;
            _context.next = 4;
            return fetchTokenInfo();
          case 4:
            data = _context.sent;
            newToken = data === null || data === void 0 || (_data$View = data.View) === null || _data$View === void 0 ? void 0 : _data$View.token;
            if (newToken && setToken) {
              setToken(newToken);
              reconnectWebSocket();
            }
            params === null || params === void 0 || (_params$onSuccess2 = params.onSuccess) === null || _params$onSuccess2 === void 0 || _params$onSuccess2.call(params);
            _context.next = 14;
            break;
          case 10:
            _context.prev = 10;
            _context.t0 = _context["catch"](1);
            params === null || params === void 0 || (_params$onFailure2 = params.onFailure) === null || _params$onFailure2 === void 0 || _params$onFailure2.call(params);
            console.error(_context.t0);
          case 14:
            _context.next = 17;
            break;
          case 16:
            reconnectWebSocket();
          case 17:
          case "end":
            return _context.stop();
        }
      }, _callee, null, [[1, 10]]);
    }));
    return function (_x) {
      return _ref.apply(this, arguments);
    };
  }(), [authZoneConfig]);
  var addEventHandler = useCallback(function (type, listener) {
    handlersRef.current[type].push(listener);
  }, []);
  var connect = useCallback( /*#__PURE__*/function () {
    var _ref2 = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee2(params) {
      return _regeneratorRuntime.wrap(function _callee2$(_context2) {
        while (1) switch (_context2.prev = _context2.next) {
          case 0:
            _context2.prev = 0;
            _context2.next = 3;
            return connectWebSocket(params);
          case 3:
            socketRef.current = _context2.sent;
            _context2.next = 10;
            break;
          case 6:
            _context2.prev = 6;
            _context2.t0 = _context2["catch"](0);
            setStatus(SocketStatus.closed);
            console.error(_context2.t0);
          case 10:
          case "end":
            return _context2.stop();
        }
      }, _callee2, null, [[0, 6]]);
    }));
    return function (_x2) {
      return _ref2.apply(this, arguments);
    };
  }(), [connectWebSocket]);
  return useMemo(function () {
    return {
      addEventHandler: addEventHandler,
      socketRef: socketRef,
      reconnect: reconnect,
      setStatus: setStatus,
      connect: connect,
      socketStatus: socketStatus,
      config: config
    };
  }, [socketStatus, reconnect, config, connect]);
};